import md5 from 'md5';
import { customUnidecode, launchConfetti } from './bagofwords.js';

// What a typed answer is compared on. A gap asks for a word or a sentence, not
// for a spelling convention: case, the hyphen a student may put before an
// ending, the full stop at the end of a translation, and ä/ae spelling are all
// noise here, and getting them wrong is not the mistake the exercise is about.
// Word order still counts — {.bagofwords} is the tool when it should not.
function normaliseAnswer(value) {
    return customUnidecode(String(value))
        .normalize('NFC')
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}

function shake(div) {
    var interval = 50;
    var distance = 5;
    var times = 1;

    div.style.position = 'relative';

    var animateShake = (iter) => {
        if (iter <= times) {
            div.style.left = (iter % 2 === 0 ? distance : distance * -1) + 'px';
            setTimeout(() => animateShake(iter + 1), interval);
        } else {
            div.style.left = '0px';
        }
    };

    animateShake(0);
}

// The answers one gap accepts: md5 hashes of the raw alternatives plus their
// normalised spellings.
function answerSet(input) {
    const md5s = (input.getAttribute('data-solution') || '').normalize('NFC')
        .split('||').filter(n => n);
    const encoded = (input.getAttribute('data-solution-base64') || '').split('||').filter(n => n);
    const solutions = encoded.map(e => normaliseAnswer(decodeURIComponent(atob(e.replace(/\s+/g, '')))));
    return { md5s, solutions };
}

function inSet(set, value) {
    const response = normaliseAnswer(value);
    return set.md5s.includes(md5(String(value).normalize('NFC')))
        || (response !== '' && set.solutions.includes(response));
}

// A gap that holds its answer: marked correct, or revealed by the exercise.
function isSettled(input) {
    return input.dataset.completed === 'true'
        || input.classList.contains('gap-input-correct')
        || input.classList.contains('gap-input-revealed');
}

// A group lives inside its exercise (or section): `group=f` reused in two
// exercises on one page is two groups, not one.
function groupMembers(input) {
    const name = input.getAttribute('data-gap-group');
    const scope = input.closest('.exercise, section') || document;
    return [...scope.querySelectorAll('input[data-gap-group]')]
        .filter(el => el.getAttribute('data-gap-group') === name);
}

// Gaps sharing data-gap-group (authored as `group=name`) are one set of
// answers in any order, as in «give all possible cases». Each member's own
// alternatives are one answer of the set; synonyms in one slot (Sg.|Singular)
// are one answer, and «Nom.Pl.» typed twice counts once.
//
// Members are matched to answers as a bipartite matching (augmenting paths),
// taken in priority order: settled gaps first, then page order. A member that
// has an answer may be moved to another answer its value also fits, but never
// loses one — so a correct gap stays correct, the first of two equal values
// wins, and overlapping alternatives (Nom.Pl.|Akk.Pl. beside Nom.Pl.) no
// longer reject an order that has a valid assignment.
//
// Returns owner: answer index → member index (or -1).
function matchGroup(members, valueOf, order) {
    const sets = members.map(answerSet);
    const fits = members.map(el => {
        const value = valueOf(el);
        return sets.map(set => value !== undefined && inSet(set, value));
    });
    const owner = sets.map(() => -1);
    const augment = (m, seen) => {
        for (let k = 0; k < sets.length; k += 1) {
            if (!fits[m][k] || seen[k]) continue;
            seen[k] = true;
            if (owner[k] === -1 || augment(owner[k], seen)) {
                owner[k] = m;
                return true;
            }
        }
        return false;
    };
    order.forEach(m => augment(m, sets.map(() => false)));
    return owner;
}

// Priority order of member indices; `self` never counts as settled — its old
// verdict must not let it keep an answer its new value may not deserve.
function priority(members, self) {
    const idx = members.map((_, i) => i);
    const settled = i => members[i] !== self && isSettled(members[i]);
    return [...idx.filter(settled), ...idx.filter(i => !settled(i))];
}

function groupClaims(input, value) {
    const members = groupMembers(input);
    const me = members.indexOf(input);
    const owner = matchGroup(members, el => (el === input ? value : el.value),
        priority(members, input));
    return owner.includes(me);
}

// Does `value` answer this gap? The same comparison the live check uses,
// shared with exercises.js so a deferred check accepts exactly what a
// per-keystroke check would have.
export function gapMatches(input, value) {
    if (!input.hasAttribute('data-gap-group')) return inSet(answerSet(input), value);
    return groupClaims(input, value);
}

function firstAlternative(input) {
    const encoded = (input.getAttribute('data-solution-base64') || '').split('||').filter(n => n);
    if (!encoded.length) return '';
    try {
        return decodeURIComponent(atob(encoded[0].replace(/\s+/g, '')));
    } catch (_) {
        return '';
    }
}

// What a reveal prints into the gap: the first listed answer, as authored.
// In a group, the first answer no other member already holds — its own if
// that is free — so a reveal never repeats an answer the student gave.
export function gapSolution(input) {
    if (!input.hasAttribute('data-gap-group')) return firstAlternative(input);
    const members = groupMembers(input);
    const me = members.indexOf(input);
    const others = priority(members, input).filter(i => i !== me);
    const owner = matchGroup(members, el => (el === input ? undefined : el.value), others);
    const free = owner[me] === -1 ? me : owner.findIndex(o => o === -1);
    return firstAlternative(members[free === -1 ? me : free]);
}

// The answer attributes for a stand-alone copy of this gap (the revisit
// block): a grouped gap accepts any answer of its group, since the copy is
// asked on its own, without the other slots. Its own answers come first, so a
// reveal of the copy prints what this slot was keyed to.
export function gapAnswerAttrs(input) {
    const members = input.hasAttribute('data-gap-group')
        ? [input, ...groupMembers(input).filter(el => el !== input)]
        : [input];
    const join = attr => [...new Set(members.flatMap(el =>
        (el.getAttribute(attr) || '').split('||').filter(n => n)))].join('||');
    return { solution: join('data-solution'), base64: join('data-solution-base64') };
}

export function initGapInputs() {
    const inputs = document.querySelectorAll('input[data-solution], input[data-solution-base64]');
    console.log('[gap] init', { count: inputs.length });

    inputs.forEach(input => {
        input.classList.add('gap-input');
        input.dataset.completed = 'false';
        // agenor.css handles display, border, background, and font.
        // Full width in a table cell only when the input is the sole content of that cell,
        // so the field width cannot be used to infer the answer length.
        const cell = input.closest('td, th');
        if (cell) {
            const clone = cell.cloneNode(true);
            clone.querySelectorAll('input').forEach(i => i.remove());
            if (clone.textContent.trim() === '') {
                input.style.width = '100%';
            }
        }

        // Live tint: yellow when filled, red (default) when empty
        input.addEventListener('input', function() {
            if (this.dataset.completed === 'true') return;
            this.classList.toggle('gap-input-filled', this.value.trim().length > 0);
        });

        input.addEventListener('keyup', function() {
            // Inside a deferred exercise the check happens on «Prüfen», not per keystroke.
            if (this.dataset.phDeferred === '1') return;
            if (gapMatches(this, this.value)) {
                this.classList.remove('gap-input-filled');
                this.classList.add('gap-input-correct');
                if (this.dataset.completed !== 'true') {
                    this.dataset.completed = 'true';
                    launchConfetti();
                }
                this.disabled = true;
                shake(this);

                const table = this.closest("table");
                const moveHorizontally = table && table.hasAttribute("data-cursor-horizontal");

                const parentRow = this.closest("tr");
                if (parentRow) {
                    const td = this.closest('td, th');
                    const tdIndex = td ? [...parentRow.children].indexOf(td) : -1;
                    let nextInput;

                    if (moveHorizontally) {
                        const nextTd = td?.nextElementSibling;
                        nextInput = nextTd?.querySelector('input:not([disabled])');

                        if (!nextInput && parentRow.nextElementSibling) {
                            const nextRow = parentRow.nextElementSibling;
                            for (const cell of nextRow.querySelectorAll('td')) {
                                const input = cell.querySelector('input:not([disabled])');
                                if (input) {
                                    nextInput = input;
                                    break;
                                }
                            }
                        }
                    } else {
                        nextInput = parentRow.nextElementSibling?.querySelectorAll('td')[tdIndex]?.querySelector('input');
                    }

                    nextInput?.focus();
                } else {
                    const allInputs = [...document.querySelectorAll('input')];
                    const currentIndex = allInputs.indexOf(this);
                    const nextInput = allInputs.slice(currentIndex + 1).find(i => !i.disabled);
                    nextInput?.focus();
                }
            } else {
                this.dataset.completed = 'false';
                this.classList.remove('gap-input-correct');
                this.classList.toggle('gap-input-filled', this.value.trim().length > 0);
            }
        });
    });
}
